import { useLazyQuery, useQuery } from "@apollo/client/react";
import { PencilIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Waiting } from "../../../../../animations";
import { ErrorFallback, Head } from "../../../../../components/core";
import { Button } from "../../../../../components/form";
import {
  GET_VEHICLE_BY_ID,
  Vehicle,
} from "../../../../../graphql/fleets/vehicles";
import {
  GET_EXPENSEENTRY_BY_ID,
  VehicleExpenseEntry,
} from "../../../../../graphql/fleets/vehicles/pages/expense-entry";
import { AuthShield } from "../../../../auth/core";
import { ExpenseEntryDetail } from "../../components/ExpenseEntryDetail";

const ExpenseEntryView = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  let { expenseEntryId } = useParams();
  const { t } = useTranslation();

  const [fetchVehicle, { data: vehicleData }] = useLazyQuery<{
    fetchVehicle: Vehicle;
  }>(GET_VEHICLE_BY_ID);

  const { data, loading, error } = useQuery<{
    fetchExpenseEntry: VehicleExpenseEntry;
  }>(GET_EXPENSEENTRY_BY_ID, {
    variables: { id: expenseEntryId },
    onCompleted(data) {
      fetchVehicle({ variables: { id: data?.fetchExpenseEntry?.vehicleId } });
    },
  });

  const expenseEntry = useMemo<VehicleExpenseEntry | undefined>(
    () => data?.fetchExpenseEntry,
    [data]
  );

  const vehicle = useMemo<Vehicle | undefined>(
    () => vehicleData?.fetchVehicle,
    [vehicleData]
  );

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={ExpenseEntryViewResource.name}
        heading={ExpenseEntryViewResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: ExpenseEntryViewResource.name,
            href: null,
          },
        ]}
      />
      <div className="max-w-medium mx-auto">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_vehicle_expense_entry")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_vehicle_expense_entry")}
              </p>
            </div>
            <div className="ml-auto flex items-center space-x-2 pr-3">
              <AuthShield access={["read-vehicles"]}>
                <Button
                  type="link"
                  href={`/fleets/vehicles/expense-entries/${expenseEntryId}/edit`}
                >
                  <PencilIcon className="mr-2 h-5 w-5" />
                  {t("text_edit")}
                </Button>
              </AuthShield>
            </div>
          </div>

          {loading || !expenseEntry ? (
            <Waiting />
          ) : (
            <ExpenseEntryDetail
              expenseEntry={expenseEntry}
              vehicleBasic={
                vehicle
                  ? {
                      id: parseInt(vehicle.id),
                      name: vehicle.name,
                      vehicleImageUrl: vehicle.vehicleImageUrl,
                      make: vehicle.make,
                      model: vehicle.model,
                      year: vehicle.year,
                      operator: vehicle.operator,
                      status: vehicle.status,
                      fuelUnit: vehicle?.settings?.length
                        ? vehicle?.settings[0].fuelUnit
                        : null,
                      measurementUnit: vehicle?.settings?.length
                        ? vehicle?.settings[0].measurementUnit
                        : null,
                      primaryMeter: vehicle?.settings?.length
                        ? vehicle?.settings[0].primaryMeter
                        : null,
                    }
                  : null
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ExpenseEntryView;
export const ExpenseEntryViewResource: ResourceProps = {
  name: "Expense Entry",
  description: "View an existing expense entry",
  access: ["read-vehicles"],
  path: "/expense-entries/:expenseEntryId",
};
